import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";

import { orderBy } from "lodash";
import { DateRangePicker } from "react-dates";
import moment from "moment";
import { Collapse, Well, Row, Col } from "react-bootstrap";
import Formsy from "formsy-react";

import DropdownSearch from "../../common/DropdownSearch";

const SourceSideFilters = ({
  filters,
  customers,
  users,
  projects,
  codes,
  departments,
  onChangeFilters,
  onChangeDate,
  onResetFilters,
  onSelectAll,
  allSelected,
  setAllSelected,
  grabbed,
  totalCount,
  services,
  loading,
  onGetCallsClick
}) => {
  const [focusedInput, setFocusedInput] = useState(null);

  const generateCustomerOptions = () => {
    const customerOptions = customers.map((customer) => ({
      value: customer._id,
      label: customer.name
    }));
    return orderBy(customerOptions, [(option) => option.label.toLowerCase()]);
  };

  const generateCodeOptions = (entity, field) => {
    const codeOptions = codes
      .filter((c) => c.entity === entity && c.field === field)
      .map((code) => ({
        value: code._id,
        label: code.label,
        order: code.order
      }));
    return orderBy(
      codeOptions,
      [(option) => option.label.toLowerCase()],
      "order"
    );
  };

  const generateDepartmentOptions = () => {
    const departmentOptions = departments.map((department) => ({
      value: department._id,
      label: department.description
    }));
    return orderBy(departmentOptions, [(option) => option.label.toLowerCase()]);
  };

  const generateWorkplaceOptions = () => {
    const workplaceOptions = codes
      .filter((code) => code.entity === "Call" && code.field === "workplace")
      .map((code) => ({ value: code._id, label: code.label }));
    return orderBy(workplaceOptions, [(option) => option.label.toLowerCase()]);
  };

  const generateServiceOptions = () => {
    const serviceOptions = services
      .filter((service) => service.status)
      .map((service) => ({
        value: service._id,
        label: service.description
      }));
    return orderBy(serviceOptions, [(option) => option.label.toLowerCase()]);
  };

  const filteredUsers = useMemo(
    () =>
      filters.department && filters.department.length
        ? users.filter(
            (user) =>
              user.department &&
              filters.department.some(
                (dept) => dept.value === user.department._id
              )
          )
        : users,
    [filters.department, users]
  );

  const filteredProjects = useMemo(
    () =>
      filters.customer && filters.customer.value
        ? projects.filter(
            (proj) =>
              proj.customer &&
              proj.customer._id === filters.customer.value &&
              proj.name !== "Personal Time"
          )
        : [],
    [filters.customer, projects]
  );

  const handleSelectAll = () => {
    setAllSelected(!allSelected);
    onSelectAll(!allSelected);
  };

  const getCallsButtonDisabled = useMemo(
    () =>
      !filters.project.value ||
      !filters.customer.value ||
      !filters.department.value ||
      loading,
    [filters.customer, filters.project, filters.department, loading]
  );

  return (
    <Collapse in className="filter-container">
      <div>
        <Well>
          <Formsy className="vertical form">
            <div className="fields">
              <DropdownSearch
                name="customer"
                placeholder="Customer *"
                onChange={onChangeFilters}
                value={filters.customer.value}
                data={[...generateCustomerOptions()]}
              />
              <DropdownSearch
                name="project"
                placeholder="Project *"
                onChange={onChangeFilters}
                value={filters.project.value}
                data={filteredProjects.map((project) => ({
                  value: project._id,
                  label: project.name
                }))}
              />
              <DropdownSearch
                placeholder="Department *"
                name="department"
                onChange={onChangeFilters}
                value={filters.department.value}
                data={[...generateDepartmentOptions()]}
              />
              <DropdownSearch
                placeholder="Assigned Tech"
                name="assigned_to"
                onChange={onChangeFilters}
                value={filters.assigned_to.value}
                data={filteredUsers.map((user) => ({
                  value: user._id,
                  label: user.full_name
                }))}
              />
              <DropdownSearch
                isMulti
                placeholder="Status"
                name="stage"
                onChange={onChangeFilters}
                value={filters.stage}
                data={[...generateCodeOptions("Call", "status")]}
              />
              <DropdownSearch
                placeholder="Service"
                name="service"
                onChange={onChangeFilters}
                value={filters.service.value}
                data={[...generateServiceOptions()]}
              />
              <DropdownSearch
                isMulti
                placeholder="Call Type"
                name="call_type"
                onChange={onChangeFilters}
                value={filters.call_type}
                data={[...generateCodeOptions("Call", "type")]}
              />
              <DateRangePicker
                startDate={
                  filters.arrival_date ? moment(filters.arrival_date) : null
                }
                startDateId="startDate"
                endDate={
                  filters.completed_date ? moment(filters.completed_date) : null
                }
                endDateId="endDate"
                onDatesChange={({ startDate, endDate }) => {
                  onChangeDate(startDate, endDate);
                }}
                isOutsideRange={() => false}
                focusedInput={focusedInput}
                onFocusChange={setFocusedInput}
              />
              <DropdownSearch
                isMulti
                placeholder="Workplace"
                name="workplace"
                onChange={onChangeFilters}
                value={filters.workplace}
                data={[...generateWorkplaceOptions()]}
              />
            </div>
            <Row className="mb-0">
              <Col md={12}>
                <button
                  className="btn btn-warning"
                  type="button"
                  onClick={onResetFilters}
                >
                  Reset Filters
                </button>
                <button
                  className="btn btn-info"
                  type="button"
                  onClick={onGetCallsClick}
                  disabled={getCallsButtonDisabled}
                >
                  Get Calls
                </button>
                <button
                  className="btn btn-info"
                  type="button"
                  onClick={handleSelectAll}
                  disabled={grabbed === 0 || loading}
                >
                  {allSelected ? "Unselect All" : "Select All"}
                </button>
              </Col>
              <Col md={12}>
                <caption>* Required</caption>
              </Col>
              {grabbed > 0 && !loading && (
                <Col md={12}>
                  <caption>
                    Showing {grabbed} of {totalCount}
                  </caption>
                </Col>
              )}
            </Row>
          </Formsy>
        </Well>
      </div>
    </Collapse>
  );
};

SourceSideFilters.propTypes = {
  filters: PropTypes.object,
  customers: PropTypes.array,
  users: PropTypes.array,
  projects: PropTypes.array,
  codes: PropTypes.array,
  departments: PropTypes.array,
  onChangeFilters: PropTypes.func,
  onChangeDate: PropTypes.func,
  onResetFilters: PropTypes.func,
  onSelectAll: PropTypes.func,
  setAllSelected: PropTypes.func,
  allSelected: PropTypes.bool,
  grabbed: PropTypes.number,
  totalCount: PropTypes.number,
  services: PropTypes.array,
  loading: PropTypes.bool,
  onGetCallsClick: PropTypes.func
};

SourceSideFilters.defaultProps = {
  filters: {},
  customers: [],
  users: [],
  projects: [],
  codes: [],
  departments: [],
  onChangeFilters: () => null,
  onChangeDate: () => null,
  onResetFilters: () => null,
  onSelectAll: () => null,
  grabbed: 0,
  totalCount: 0,
  services: [],
  loading: false,
  onGetCallsClick: () => null
};

function mapStatesToProps(state) {
  return {
    customers: state.reducers.customers,
    users: state.reducers.users,
    projects: state.reducers.projects,
    codes: state.reducers.codes,
    departments: state.reducers.departments,
    services: state.reducers.services
  };
}

export default connect(mapStatesToProps, null)(SourceSideFilters);
